import type { FC } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CheckIcon from 'mdi-react/CheckIcon'
import CloseIcon from 'mdi-react/CloseIcon'
import { useNavigate, useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { LocalDateTime } from '@/components/LocalDateTime'
import useUser from '@/hooks/user'
import salaryDeductionApplicationResources from '@/modules/applications/salary-deduction/resources'
import { phoneNumber } from '@/util/formatters/text'

import { SDA_STATUS_ID, SDA_STATUS_NAME } from './constants'
import queries from './queries'

import './SalaryDeductionPage.styles.scss'

export const Sda: FC = () => {
  const navigate = useNavigate()

  const { id } = useParams()
  const user = useUser()
  const queryClient = useQueryClient()

  const {
    data: deduction,
    isLoading,
    refetch: updateSalaryDeduction,
  } = useQuery(queries.salaryDeductionApplication(Number(id)), () =>
    salaryDeductionApplicationResources.get(Number(id))
  )

  const setStatusMutation = useMutation(
    (statusId: number) =>
      salaryDeductionApplicationResources.setStatus({ applicationId: Number(id), statusId }),
    {
      onSuccess() {
        updateSalaryDeduction()
        queryClient.invalidateQueries(queries.salaryDeductionApplications(), {
          // todo: change when the rq updateOnMount is on again
          refetchType: 'all',
        })
      },
    }
  )

  const statusId = deduction?.status
  const isAutoHandled = deduction?.isAutoHandled
  const isAutoApproved = isAutoHandled && statusId === SDA_STATUS_ID.APPROVED
  const isAutoRejected = isAutoHandled && statusId === SDA_STATUS_ID.DENIED

  if (isLoading) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <div className="salary-deduction-application__load-wrapper">
          <LoaderCircle />
        </div>
      </Container>
    )
  }

  if (!deduction) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <Translation id="Cannot find application..." />
      </Container>
    )
  }

  const userMayChangeApplicationStatus =
    user?.mayApproveOwnApplications || deduction.costObjectId !== user?.costObjectId
  return (
    <Container className="salary-deduction-application" onSwipedLeft={() => navigate(-1)}>
      <Container.Header>
        <Translation id="Salary Deduction Application" /> {deduction.id}
      </Container.Header>

      <h3>
        <Translation id={`sda_status_name/${SDA_STATUS_NAME[deduction.status]}`} />
      </h3>

      {deduction.status === SDA_STATUS_ID.NEW && (
        <div className="salary-deduction-application__change">
          {userMayChangeApplicationStatus && (
            <>
              <Button
                variant="primary"
                Icon={<CheckIcon />}
                onClick={() => setStatusMutation.mutate(SDA_STATUS_ID.APPROVED)}
              >
                <Translation id="Approve" />
              </Button>

              <Button
                variant="danger"
                Icon={<CloseIcon />}
                onClick={() => setStatusMutation.mutate(SDA_STATUS_ID.DENIED)}
              >
                <Translation id="Deny" />
              </Button>
            </>
          )}
        </div>
      )}

      <div className="sections">
        <section>
          <DataPoints>
            <h3>
              <Translation id="User" />
            </h3>

            <DataPoint
              header={<Translation id="Name" className="capitalize" />}
              value={deduction.user}
            />

            <DataPoint
              header={<Translation id="Phone number" />}
              value={phoneNumber(deduction.code)}
            />

            <DataPoint header={<Translation id="Manager" />} value={deduction.manager} />
          </DataPoints>
        </section>

        <section>
          <DataPoints>
            <h3>
              <Translation id="Application" />
            </h3>

            <DataPoint
              header={<Translation id="Amount" />}
              value={<FormattedNumberWithCurrency amount={deduction.amount} precision={2} />}
            />

            <DataPoint header={<Translation id="Product" />} value={deduction.product} />

            <DataPoint
              header={<Translation id="Requested covered amount" />}
              value={<FormattedNumberWithCurrency amount={deduction.sdaAmount} precision={2} />}
            />

            <DataPoint header={<Translation id="Comment" />} value={deduction.comment} />

            {deduction.updatedDatetime && (
              <DataPoint
                header={<Translation id="Last updated at" />}
                value={<LocalDateTime date={deduction.updatedDatetime} />}
              />
            )}

            {isAutoApproved && (
              <DataPoint
                header={<Translation id="Last updated by" />}
                value={<Translation id="Auto-approved" />}
              />
            )}

            {isAutoRejected && (
              <DataPoint
                header={<Translation id="Last updated by" />}
                value={<Translation id="Auto-rejected" />}
              />
            )}

            {deduction.updatedUser && !isAutoHandled && (
              <DataPoint
                header={<Translation id="Last updated by" />}
                value={deduction.updatedUser}
              />
            )}
          </DataPoints>
        </section>
      </div>

      <Button variant="tertiary" onClick={() => navigate(-1)}>
        <Translation id="Go Back" />
      </Button>
    </Container>
  )
}
